import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  LogsPanel,
  type LogEntry,
  type LogLevel,
} from '@/components'
import { botApi, type BacktestLogEntry } from '../api'
import { useSSE } from '../../contexts/SSEContext'
import { SSE_EVENTS } from '../../contexts/SSEEvents'

interface BotLogsProps {
  btid: string
}

const DEFAULT_PAGE_SIZE = 10
const LEVELS: LogLevel[] = ['info', 'warn', 'error']
const LEVEL_LABELS: Partial<Record<LogLevel, string>> = { warn: 'warning' }

const BACKTEST_DONE_STATUSES = new Set([
  'BACKTEST_COMPLETED',
  'BACKTEST_ERROR',
  'BACKTEST_CANCELED',
])

function normalizeLevel(raw: string): LogLevel {
  const v = (raw ?? '').toLowerCase()
  if (v === 'info') return 'info'
  if (v === 'warning' || v === 'warn') return 'warn'
  if (v === 'error') return 'error'
  if (v === 'debug') return 'debug'
  return 'info'
}

function splitEventTime(iso: string): { date: string; timestamp: string } {
  // Backend returns ISO-ish datetimes like "2024-10-26T14:32:15",
  // "2024-10-26T14:32:15.123" or "2024-10-26 14:32:15Z". Pull date + HH:MM:SS
  // out; fall back to the raw string on shape mismatch.
  const trimmed = iso.replace(/\.\d+/, '').replace(/Z$/, '')
  const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/)
  if (match) return { date: match[1], timestamp: match[2] }
  return { date: trimmed, timestamp: '' }
}

function toLogEntry(e: BacktestLogEntry): LogEntry {
  const { date, timestamp } = splitEventTime(e.event_time)
  return {
    id: e.event_id,
    date,
    timestamp,
    level: normalizeLevel(e.level),
    message: e.message,
  }
}

interface PrevQuery {
  btid: string
  page: number
  pageSize: number
  refresh: number
}

interface BacktestStatusSSEMessage {
  event: string
  btid?: string
  toolStatus?: string
}

export default function BotLogs({ btid }: BotLogsProps) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [events, setEvents] = useState<BacktestLogEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [refresh, setRefresh] = useState(0)

  // "Store information from previous renders" pattern — mirrors BotTrades —
  // so we can reset rows + flip loading=true when btid / page / pageSize /
  // refresh change without calling setState inside useEffect.
  const [prevQuery, setPrevQuery] = useState<PrevQuery | null>(null)
  if (
    prevQuery === null ||
    prevQuery.btid !== btid ||
    prevQuery.page !== page ||
    prevQuery.pageSize !== pageSize ||
    prevQuery.refresh !== refresh
  ) {
    const btidChanged = prevQuery?.btid !== btid
    setPrevQuery({ btid, page, pageSize, refresh })
    setLoading(true)
    setErrorMessage(null)
    if (btidChanged) {
      setEvents([])
      setTotal(0)
      if (page !== 0) setPage(0)
    }
  }

  useSSE(
    useCallback(
      (msg: BacktestStatusSSEMessage) =>
        msg.event === SSE_EVENTS.BOT_BACKTEST_STATUS &&
        msg.btid === btid &&
        !!msg.toolStatus &&
        BACKTEST_DONE_STATUSES.has(msg.toolStatus),
      [btid],
    ),
    useCallback(() => {
      setRefresh((prev) => prev + 1)
    }, []),
  )

  useEffect(() => {
    let cancelled = false
    botApi
      .getBacktestLogs(btid, page + 1, pageSize)
      .then((data) => {
        if (cancelled) return
        setEvents(data.events)
        setTotal(data.total)
      })
      .catch((err) => {
        if (cancelled) return
        const message =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Failed to load logs'
        setErrorMessage(message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [btid, page, pageSize, refresh])

  const mapped = useMemo<LogEntry[]>(() => events.map(toLogEntry), [events])

  if (errorMessage) {
    return (
      <div className="flex items-center justify-center h-full px-6 text-body text-danger text-center">
        {errorMessage}
      </div>
    )
  }

  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-body text-muted-foreground gap-2">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current" />
        <span>Loading logs...</span>
      </div>
    )
  }

  if (total === 0) {
    return <LogsPanel logs={[]} isEmpty emptyLabel="No log entries yet" />
  }

  return (
    <LogsPanel
      logs={mapped}
      levels={LEVELS}
      levelLabels={LEVEL_LABELS}
      page={page}
      pageSize={pageSize}
      totalRows={total}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size)
        setPage(0)
      }}
    />
  )
}
